<template>
	<div>
		<dl class="row row-cols-1 row-cols-md-2 g-2 mb-0 small">
			<div class="col">
				<dt class="text-body-secondary fw-normal">Kind</dt>
				<dd class="mb-0">
					<span
						class="badge rounded-pill"
						:class="kindBadgeClass(assignment.kind)"
					>
						{{ kindLabel(assignment.kind) }}
					</span>
				</dd>
			</div>
			<div class="col">
				<dt class="text-body-secondary fw-normal">State</dt>
				<dd class="mb-0">
					<span
						class="badge rounded-pill"
						:class="stateBadgeClass(assignment.state)"
					>
						{{ stateLabel(assignment.state) }}
					</span>
				</dd>
			</div>
			<div class="col">
				<dt class="text-body-secondary fw-normal">Technician</dt>
				<dd class="mb-0">
					{{ technicianLabel(assignment.technicianId, users) }}
				</dd>
			</div>
			<div class="col">
				<dt class="text-body-secondary fw-normal">Window</dt>
				<dd class="mb-0">{{ assignmentWindowText(assignment) }}</dd>
			</div>
			<div class="col">
				<dt class="text-body-secondary fw-normal">Service time</dt>
				<dd class="mb-0">{{ serviceTimeText(assignment.serviceMinutes) }}</dd>
			</div>
			<div class="col">
				<dt class="text-body-secondary fw-normal">Target</dt>
				<dd class="mb-0">{{ targetLabel(assignment, sites) }}</dd>
			</div>
			<div class="col">
				<dt class="text-body-secondary fw-normal">Route</dt>
				<dd class="mb-0">
					<template v-if="assignment.routeId !== null">
						<button
							type="button"
							class="btn btn-link btn-sm p-0 align-baseline"
							@click="emit('select', { kind: 'route', id: assignment.routeId })"
						>
							Route #{{ assignment.routeId }}
						</button>
						<span v-if="assignment.routePosition !== null">
							at slot {{ assignment.routePosition + 1 }}
						</span>
						<span v-else class="text-body-secondary">(unsequenced)</span>
					</template>
					<span v-else class="text-body-secondary">Not on a route</span>
				</dd>
			</div>
			<div v-if="configurationName" class="col">
				<dt class="text-body-secondary fw-normal">Configuration</dt>
				<dd class="mb-0">{{ configurationName }}</dd>
			</div>
			<div class="col">
				<dt class="text-body-secondary fw-normal">Created</dt>
				<dd class="mb-0">{{ formatDate(new Date(assignment.createdAt)) }}</dd>
			</div>
			<div v-if="assignment.completedAt" class="col">
				<dt class="text-body-secondary fw-normal">Completed</dt>
				<dd class="mb-0">{{ formatDate(new Date(assignment.completedAt)) }}</dd>
			</div>
			<div v-if="assignment.instructions" class="col-12">
				<dt class="text-body-secondary fw-normal">Instructions</dt>
				<dd class="mb-0" style="white-space: pre-wrap">
					{{ assignment.instructions }}
				</dd>
			</div>
			<div v-if="assignment.stateReason" class="col-12">
				<dt class="text-body-secondary fw-normal">Reason recorded</dt>
				<dd class="mb-0">{{ assignment.stateReason }}</dd>
			</div>
		</dl>

		<!-- §5.4: the access window and the service time are planning inputs, so
		     they stay editable while the work and its Route are both draft and
		     freeze once the day is dispatched. The whole window is sent rather
		     than a diff, because this card shows what the row holds: a field the
		     operator blanks is an explicit clear. -->
		<div class="mt-3">
			<h3 class="h6">Access window</h3>
			<template v-if="scheduleEditable">
				<div class="row g-2">
					<div class="col-12 col-md-6">
						<label class="form-label small" for="assignmentScheduleStart">
							Start
						</label>
						<input
							id="assignmentScheduleStart"
							v-model="scheduleStart"
							type="datetime-local"
							class="form-control form-control-sm"
							:disabled="busy"
						/>
					</div>
					<div class="col-12 col-md-6">
						<label class="form-label small" for="assignmentScheduleEnd">
							End
						</label>
						<input
							id="assignmentScheduleEnd"
							v-model="scheduleEnd"
							type="datetime-local"
							class="form-control form-control-sm"
							:disabled="busy"
						/>
					</div>
					<div class="col-12 col-md-6">
						<label class="form-label small" for="assignmentScheduleMinutes">
							Service time (minutes)
						</label>
						<input
							id="assignmentScheduleMinutes"
							v-model="scheduleMinutes"
							type="number"
							min="1"
							max="720"
							step="1"
							class="form-control form-control-sm"
							placeholder="District default"
							:disabled="busy"
						/>
					</div>
				</div>
				<div class="d-flex align-items-center gap-2 mt-2">
					<button
						type="button"
						class="btn btn-sm btn-outline-primary"
						:disabled="busy"
						@click="saveSchedule()"
					>
						Save window
					</button>
					<span class="text-body-secondary small">
						A blank start or end clears it; the drive plan reads whatever these
						hold.
					</span>
				</div>
			</template>
			<p v-else class="text-body-secondary small mb-0">
				{{ scheduleLockedReason }}
			</p>
			<div v-if="scheduleError" class="alert alert-danger py-2 small mt-2 mb-0">
				{{ scheduleError }}
			</div>
		</div>

		<div class="d-flex flex-wrap gap-2 mt-3">
			<button
				v-for="target in targets"
				:key="target"
				type="button"
				class="btn btn-sm btn-outline-primary"
				:disabled="busy"
				@click="choose(target)"
			>
				Move to {{ stateLabel(target) }}
			</button>
		</div>

		<!-- §7 demands a recorded reason for deferred and cancelled; the field
		     appears only once one of those is chosen. -->
		<div v-if="pendingTarget" class="mt-2">
			<label class="form-label small fw-semibold" for="assignmentReason">
				Why is this moving to {{ stateLabel(pendingTarget) }}?
			</label>
			<textarea
				id="assignmentReason"
				v-model="reason"
				class="form-control form-control-sm"
				rows="2"
				placeholder="The reason is recorded with the state."
			></textarea>
			<div class="d-flex gap-2 mt-2">
				<button
					type="button"
					class="btn btn-sm btn-primary"
					:disabled="busy || reason.trim() === ''"
					@click="confirmReason()"
				>
					Move to {{ stateLabel(pendingTarget) }}
				</button>
				<button
					type="button"
					class="btn btn-sm btn-outline-secondary"
					:disabled="busy"
					@click="cancelReason"
				>
					Cancel
				</button>
			</div>
		</div>

		<p v-if="targets.length === 0" class="text-body-secondary small mb-0 mt-3">
			{{
				assignment.state === "completed" || assignment.state === "cancelled"
					? "This assignment is settled; §7 offers no edge out of it."
					: "§7 offers no move from this state."
			}}
		</p>

		<!-- The Route a draft Assignment joins. The door binds a Route to one
		     technician, so only that technician's editable routes are offered. -->
		<div
			v-if="assignment.routeId === null && editableRoutes.length > 0"
			class="mt-3"
		>
			<label class="form-label small fw-semibold" for="assignmentRoute">
				Add to a route
			</label>
			<div class="input-group input-group-sm">
				<select
					id="assignmentRoute"
					v-model.number="addRouteId"
					class="form-select"
					:disabled="busy"
				>
					<option :value="0" disabled>Choose a route…</option>
					<option
						v-for="route in editableRoutes"
						:key="route.id"
						:value="route.id"
					>
						#{{ route.id }} — {{ routeWindowText(route) }}
					</option>
				</select>
				<button
					type="button"
					class="btn btn-outline-primary"
					:disabled="busy || addRouteId <= 0"
					@click="addToRoute()"
				>
					Add
				</button>
			</div>
		</div>

		<!-- A refused move keeps the server's words: a completion refusal names
		     the evidence that is missing, and rewording it would hide the one
		     instruction the operator needs. -->
		<div v-if="transitionError" class="alert alert-danger py-2 small mt-3 mb-0">
			{{ transitionError }}
		</div>
		<div v-else-if="routeError" class="alert alert-danger py-2 small mt-3 mb-0">
			{{ routeError }}
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { apiClient } from "@/client";
import { formatDate } from "@/format";
import type {
	AssignmentScheduleUpdate,
	AssignmentState,
	AssignmentStateUpdate,
	MapReviewSiteRow,
	SyncAssignment,
	SyncRoute,
	TrapConfigurationResult,
	User,
} from "@/type/api";

import type { Selection } from "./model";
import {
	assignmentWindowText,
	describeError,
	kindBadgeClass,
	kindLabel,
	localInputValue,
	routeWindowText,
	stateBadgeClass,
	stateLabel,
	targetLabel,
	technicianLabel,
	transitionTargets,
	wireInstant,
} from "./wire";

const props = defineProps<{
	assignment: SyncAssignment;
	users: User[];
	sites: MapReviewSiteRow[];
	trapConfigurations: TrapConfigurationResult[];
	routes: SyncRoute[];
}>();

const emit = defineEmits<{
	notify: [title: string, message: string];
	changed: [];
	select: [selection: Selection];
}>();

const pendingTarget = ref<AssignmentState | null>(null);
const reason = ref("");
const busy = ref(false);
const transitionError = ref("");
const routeError = ref("");
const addRouteId = ref(0);

// The access-window editor's own fields, seeded from the row the card was
// opened on and reset when the selection moves (§5.4).
const scheduleStart = ref(instantInputValue(props.assignment.startAt));
const scheduleEnd = ref(instantInputValue(props.assignment.endAt));
const scheduleMinutes = ref(
	props.assignment.serviceMinutes === null
		? ""
		: String(props.assignment.serviceMinutes),
);
const scheduleError = ref("");

const targets = computed(() => transitionTargets(props.assignment.state));

/** The Route the Assignment belongs to, when the page has it loaded. The
 * schedule door is gated on that Route's state as well as the Assignment's, so
 * the editor is only offered when that state is known to be draft. */
const memberRoute = computed(
	() =>
		props.routes.find((route) => route.id === props.assignment.routeId) ?? null,
);

/** Whether the window and the service time may be edited at all. A draft row on
 * no Route is editable; on a Route it is editable only while that Route is draft
 * too, because a dispatched day's windows are what it was dispatched under. */
const scheduleEditable = computed(() => {
	if (props.assignment.state !== "draft") return false;
	if (props.assignment.routeId === null) return true;
	return memberRoute.value?.state === "draft";
});

/** The one line the card shows in place of the editor: why these fields are
 * fixed, in the state that fixed them. */
const scheduleLockedReason = computed(() => {
	if (props.assignment.state !== "draft") {
		return (
			`This assignment is ${stateLabel(props.assignment.state).toLowerCase()}, ` +
			"so its window and service time are fixed: they were the plan it ran under."
		);
	}
	const route = memberRoute.value;
	if (route === null) {
		return (
			`Route #${props.assignment.routeId} is not one this page has loaded, so ` +
			"its members' windows cannot be edited here."
		);
	}
	return (
		`Route #${route.id} is ${route.state}, so its members' windows and service ` +
		"times are fixed."
	);
});

const configurationName = computed(() => {
	if (props.assignment.trapConfigurationId === null) return "";
	const configuration = props.trapConfigurations.find(
		(candidate) => candidate.id === props.assignment.trapConfigurationId,
	);
	return configuration
		? `${configuration.name} (v${configuration.version})`
		: `Configuration #${props.assignment.trapConfigurationId}`;
});

const editableRoutes = computed(() =>
	props.routes.filter(
		(route) =>
			route.technicianId === props.assignment.technicianId &&
			route.state !== "completed" &&
			route.state !== "cancelled",
	),
);

/** A stored instant as a `datetime-local` value in the operator's zone — the
 * inverse of wireInstant, and the same rendering `defaultWindow()` uses. */
function instantInputValue(instant: string | null): string {
	if (instant === null) return "";
	const date = new Date(instant);
	return Number.isNaN(date.getTime()) ? "" : localInputValue(date);
}

/** The service time in the words the card prints: an override in minutes, or
 * the fact that the district's per-kind default applies. */
function serviceTimeText(minutes: number | null): string {
	return minutes === null ? "District default" : `${minutes} min`;
}

/** The service time the operator stated, or null when the field is blank. Blank
 * is an explicit clear here (the whole window is sent), which is why it is not
 * read as 0 — 0 is outside the column's range. */
function scheduleMinutesValue(): number | null {
	const raw = scheduleMinutes.value.trim();
	if (raw === "") return null;
	const parsed = Number(raw);
	return Number.isFinite(parsed) ? parsed : null;
}

/** What is wrong with the editor as it stands, or the empty string. The server
 * holds the same rules; stating them here saves a round trip. */
function scheduleValidationError(): string {
	const start = wireInstant(scheduleStart.value);
	const end = wireInstant(scheduleEnd.value);
	if (end !== null && start === null) {
		return "An end needs a start.";
	}
	if (start !== null && end !== null && new Date(end) < new Date(start)) {
		return "The end must not be before the start.";
	}
	const minutes = scheduleMinutesValue();
	if (
		minutes !== null &&
		(!Number.isInteger(minutes) || minutes < 1 || minutes > 720)
	) {
		return "Service time is a whole number of minutes between 1 and 720, or blank.";
	}
	return "";
}

/** Refill the editor from the row on screen — used when the selection moves, so
 * a half-typed window never lands on the next Assignment. */
function resetScheduleFields() {
	scheduleStart.value = instantInputValue(props.assignment.startAt);
	scheduleEnd.value = instantInputValue(props.assignment.endAt);
	scheduleMinutes.value =
		props.assignment.serviceMinutes === null
			? ""
			: String(props.assignment.serviceMinutes);
	scheduleError.value = "";
}

/** The draft-only schedule door (§5.4). The whole window is sent, so what the
 * card shows is what the row holds and a blank field clears its column. A
 * refusal keeps the server's words — it names the state that froze the fields —
 * and the page re-reads only after a write that landed. */
async function saveSchedule() {
	const invalid = scheduleValidationError();
	if (invalid !== "") {
		scheduleError.value = invalid;
		return;
	}
	busy.value = true;
	scheduleError.value = "";
	try {
		const body: AssignmentScheduleUpdate = {
			start_at: wireInstant(scheduleStart.value),
			end_at: wireInstant(scheduleEnd.value),
			service_minutes: scheduleMinutesValue(),
		};
		const updated = await apiClient.JSONPut<SyncAssignment>(
			`/api/assignment/${props.assignment.id}/schedule`,
			body,
		);
		emit(
			"notify",
			`Assignment #${updated.id} window saved`,
			`${assignmentWindowText(updated)}; ${serviceTimeText(
				updated.serviceMinutes,
			).toLowerCase()}.`,
		);
		emit("changed");
	} catch (e: unknown) {
		scheduleError.value = describeError(e);
		emit("notify", "Access window not saved", scheduleError.value);
	} finally {
		busy.value = false;
	}
}

// A reload may move the assignment to another state (or another Route); the
// pending reason belongs to the row that was on screen when it was typed.
watch(
	() => props.assignment.id,
	() => {
		pendingTarget.value = null;
		reason.value = "";
		transitionError.value = "";
		routeError.value = "";
		addRouteId.value = 0;
		resetScheduleFields();
	},
);

function choose(target: AssignmentState) {
	transitionError.value = "";
	if (target === "deferred" || target === "cancelled") {
		pendingTarget.value = target;
		reason.value = "";
		return;
	}
	void move(target, null);
}

function cancelReason() {
	pendingTarget.value = null;
	reason.value = "";
}

async function confirmReason() {
	if (!pendingTarget.value) return;
	const text = reason.value.trim();
	if (text === "") return;
	await move(pendingTarget.value, text);
}

// The one state door. A refusal is shown as the server stated it — a completion
// refusal names the record that is missing — and the page re-reads only after a
// move that succeeded, so a refused edge leaves the row exactly as it was.
async function move(to: AssignmentState, reasonText: string | null) {
	busy.value = true;
	transitionError.value = "";
	try {
		const body: AssignmentStateUpdate = { to, reason: reasonText };
		const updated = await apiClient.JSONPut<SyncAssignment>(
			`/api/assignment/${props.assignment.id}/state`,
			body,
		);
		pendingTarget.value = null;
		reason.value = "";
		emit(
			"notify",
			`Assignment #${updated.id} ${stateLabel(updated.state).toLowerCase()}`,
			updated.completedAt
				? `Completed at ${formatDate(new Date(updated.completedAt))}.`
				: `Moved from ${stateLabel(props.assignment.state).toLowerCase()}.`,
		);
		emit("changed");
	} catch (e: unknown) {
		transitionError.value = describeError(e);
		emit("notify", "Assignment state change refused", transitionError.value);
	} finally {
		busy.value = false;
	}
}

async function addToRoute() {
	if (addRouteId.value <= 0) return;
	busy.value = true;
	routeError.value = "";
	try {
		await apiClient.JSONPut(`/api/route/${addRouteId.value}/assignment`, {
			assignment_id: props.assignment.id,
			position: null,
		});
		emit(
			"notify",
			"Added to route",
			`Assignment #${props.assignment.id} joined route #${addRouteId.value} unsequenced.`,
		);
		addRouteId.value = 0;
		emit("changed");
	} catch (e: unknown) {
		routeError.value = describeError(e);
		emit("notify", "Assignment not added", routeError.value);
	} finally {
		busy.value = false;
	}
}
</script>
